import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DollarSign, Wallet, RefreshCw, Loader2, Send, CheckCircle2, Search, Copy } from 'lucide-react';
import { API } from '../App';

const STATUS_TABS = [
  { value: 'unpaid', label: 'Owed' },
  { value: 'paid', label: 'Paid' },
  { value: 'all', label: 'All' },
];

function fmtUsd(n) {
  if (n === null || n === undefined) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function timeAgo(iso) {
  if (!iso) return '—';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ReferrerPayoutsAdmin() {
  const [status, setStatus] = useState('unpaid');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Pay dialog state
  const [payOpen, setPayOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payForm, setPayForm] = useState({ payout_method: 'stripe', payout_reference: '', payout_note: '' });
  const [saving, setSaving] = useState(false);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCode, setDetailCode] = useState(null);
  const [detailUses, setDetailUses] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/referrals/payouts`, { params: { status } });
      setData(r.data);
    } catch (_e) {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openPay = (row) => {
    setPayRow(row);
    setPayForm({
      payout_method: row.payout_method || 'stripe',
      payout_reference: '',
      payout_note: '',
    });
    setPayOpen(true);
  };

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailCode(row.code);
    setDetailUses([]);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/referrals/payouts/${row.code}/uses`);
      setDetailUses(r.data?.uses || []);
    } catch (_e) {
      toast.error('Failed to load referrer detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const submitPay = async () => {
    if (!payRow) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/referrals/payouts/mark-paid`, {
        code: payRow.code,
        all_unpaid: true,
        payout_method: payForm.payout_method,
        payout_reference: payForm.payout_reference,
        payout_note: payForm.payout_note,
      });
      toast.success(r.data?.message || 'Marked paid');
      setPayOpen(false);
      setPayRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setSaving(false);
    }
  };

  const paySingleUse = async (useId) => {
    if (!window.confirm('Mark this single commission as paid?')) return;
    try {
      const r = await axios.post(`${API}/admin/referrals/payouts/${useId}/mark-paid`);
      toast.success(r.data?.message || 'Marked paid');
      // Reload both the top-level and the open detail dialog
      load();
      if (detailCode) {
        const rr = await axios.get(`${API}/admin/referrals/payouts/${detailCode}/uses`);
        setDetailUses(rr.data?.uses || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const copyContact = (row) => {
    const txt = `${row.referrer_name}${row.organization ? ' · ' + row.organization : ''}\n${row.email || ''}\n${row.phone || ''}\nCode: ${row.code}\nAmount owed: ${fmtUsd(row.commission_total)}`;
    navigator.clipboard.writeText(txt);
    toast.success('Contact copied');
  };

  const filtered = (data?.rows || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.code.toLowerCase().includes(q) ||
      (r.referrer_name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.organization || '').toLowerCase().includes(q)
    );
  });

  const totalOwed = data?.total_owed ?? 0;

  return (
    <div className="space-y-6" data-testid="referrer-payouts-admin">
      {/* Top strip */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card data-testid="payouts-summary-total-owed">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-orange-600">Currently owed</div>
            <div className="text-3xl font-medium mt-1 text-orange-600">{fmtUsd(status === 'unpaid' ? totalOwed : (data?.rows || []).filter(r=>true).reduce((a,r)=>a+(r.commission_total||0),0))}</div>
            <div className="text-xs text-muted-foreground mt-1">across {data?.rows?.length ?? 0} referrer{(data?.rows?.length ?? 0) === 1 ? '' : 's'}</div>
          </CardContent>
        </Card>
        <Card data-testid="payouts-summary-orders">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Attributed orders</div>
            <div className="text-3xl font-medium mt-1">{(data?.rows || []).reduce((a, r) => a + (r.order_count || 0), 0)}</div>
          </CardContent>
        </Card>
        <Card data-testid="payouts-summary-rate">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Default commission</div>
            <div className="text-3xl font-medium mt-1">5.00%</div>
            <div className="text-xs text-muted-foreground mt-1">on every referred order</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" /> Referrer Payouts
            </CardTitle>
            <CardDescription>
              Pay referrers out-of-band (Stripe transfer, Zelle, PayPal, cash) then click Mark Paid to close the ledger.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_TABS.map((t) => (
              <Button
                key={t.value}
                variant={status === t.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setStatus(t.value)}
                data-testid={`payouts-tab-${t.value}`}
              >
                {t.label}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="payouts-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 relative max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code, referrer name, email…"
              className="pl-9"
              data-testid="payouts-search"
            />
          </div>

          {loading ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading payouts…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="payouts-empty">
              {status === 'unpaid'
                ? 'No unpaid commissions. All caught up. 🎉'
                : 'No referral commissions in this view yet.'}
            </div>
          ) : (
            <Table data-testid="payouts-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Last order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow key={row.code} data-testid={`payouts-row-${row.code}`}>
                    <TableCell>
                      <div className="font-medium">{row.referrer_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.organization && <span>{row.organization} · </span>}
                        {row.email || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{row.code}</Badge>
                      <div className="text-xs text-muted-foreground mt-1">{row.commission_pct}%</div>
                    </TableCell>
                    <TableCell className="text-right">{row.order_count}</TableCell>
                    <TableCell className="text-right font-medium">{fmtUsd(row.commission_total)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{timeAgo(row.last_used_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyContact(row)}
                          title="Copy contact + amount"
                          data-testid={`payouts-copy-${row.code}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDetail(row)}
                          data-testid={`payouts-detail-${row.code}`}
                        >
                          Detail
                        </Button>
                        {status !== 'paid' && row.commission_total > 0 && (
                          <Button
                            size="sm"
                            onClick={() => openPay(row)}
                            data-testid={`payouts-pay-${row.code}`}
                          >
                            <Send className="h-4 w-4 mr-1" /> Mark paid
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Mark-paid dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark paid — {payRow?.code}</DialogTitle>
          </DialogHeader>
          {payRow && (
            <div className="space-y-4 text-sm">
              <div className="p-3 bg-muted/40 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Referrer</span>
                  <span className="font-medium">{payRow.referrer_name}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-muted-foreground">Orders</span>
                  <span>{payRow.order_count}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-muted-foreground">Amount to pay</span>
                  <span className="font-medium text-primary text-base">{fmtUsd(payRow.commission_total)}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Payout method</label>
                <Select value={payForm.payout_method} onValueChange={(v) => setPayForm((f) => ({ ...f, payout_method: v }))}>
                  <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe transfer</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="ach">ACH / Bank wire</SelectItem>
                    <SelectItem value="cash">Cash / cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Payout reference (optional)</label>
                <Input
                  value={payForm.payout_reference}
                  onChange={(e) => setPayForm((f) => ({ ...f, payout_reference: e.target.value }))}
                  placeholder="Stripe txn id, Zelle confirmation, cheque #"
                  data-testid="pay-reference"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Note (optional)</label>
                <Input
                  value={payForm.payout_note}
                  onChange={(e) => setPayForm((f) => ({ ...f, payout_note: e.target.value }))}
                  placeholder="Batch payout for June"
                  data-testid="pay-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={submitPay} disabled={saving} data-testid="pay-confirm-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Confirm — mark {fmtUsd(payRow?.commission_total)} paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> {detailCode} — commissions
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
          ) : detailUses.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No commissions yet.</div>
          ) : (
            <Table data-testid="payout-detail-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead className="text-right">Order total</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailUses.map((u) => (
                  <TableRow key={u.use_id}>
                    <TableCell className="text-xs font-mono">
                      {u.order_id?.slice(0, 16) || '—'}
                      <div className="text-muted-foreground mt-1 not-italic">
                        {u.used_at ? new Date(u.used_at).toLocaleDateString() : ''}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{fmtUsd(u.order_total)}</TableCell>
                    <TableCell className="text-right font-medium">{fmtUsd(u.commission_amount)}</TableCell>
                    <TableCell>
                      {u.paid_out ? (
                        <Badge className="bg-green-600 hover:bg-green-700">
                          Paid {u.paid_at ? '· ' + timeAgo(u.paid_at) : ''}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-orange-300 text-orange-700">Unpaid</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!u.paid_out && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => paySingleUse(u.use_id)}
                          data-testid={`pay-single-${u.use_id}`}
                        >
                          Mark paid
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
